import React, { useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Props { currentUrl: string; onUploaded: (url: string) => void; }

export default function ImageUploader({ currentUrl, onUploaded }: Props) {
  const inputRef                  = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [preview,   setPreview]   = useState<string>(currentUrl);
  const [error,     setError]     = useState('');

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) return setError('Image uniquement (JPG, PNG, WEBP).');
    if (file.size > 5 * 1024 * 1024)    return setError('Max 5 Mo.');
    setError(''); setUploading(true);
    setPreview(URL.createObjectURL(file));

    const ext      = file.name.split('.').pop() || 'jpg';
    const path     = `products/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error: upErr } = await supabase.storage.from('product-images').upload(path, file, { cacheControl:'3600', upsert:false });

    if (upErr) { setError('Erreur : ' + upErr.message); setPreview(currentUrl); setUploading(false); return; }

    const { data } = supabase.storage.from('product-images').getPublicUrl(path);
    onUploaded(data.publicUrl);
    setUploading(false);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
      {/* Zone preview / drop */}
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          position:'relative', width:'100%', aspectRatio:'16/9',
          borderRadius:'10px', overflow:'hidden',
          background:'var(--surface2)',
          border:`2px dashed ${preview ? 'transparent' : 'var(--border)'}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          cursor: uploading ? 'wait' : 'pointer',
        }}
      >
        {preview
          ? <img src={preview} alt="Aperçu" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : (
            <div style={{ textAlign:'center', color:'var(--muted)', padding:'1rem' }}>
              <div style={{ fontSize:'2rem', marginBottom:'0.3rem' }}>{uploading ? '⏳' : '📷'}</div>
              <div style={{ fontSize:'0.78rem' }}>{uploading ? 'Upload…' : 'Appuyer pour choisir une photo'}</div>
              <div style={{ fontSize:'0.65rem', opacity:0.6, marginTop:'0.2rem' }}>JPG · PNG · WEBP · max 5 Mo</div>
            </div>
          )
        }
        {preview && uploading && (
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.82rem', color:'#fff' }}>
            ⏳ Upload…
          </div>
        )}
        {preview && !uploading && (
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.55)', padding:'0.4rem', textAlign:'center', fontSize:'0.72rem', color:'rgba(255,255,255,0.8)' }}>
            Appuyer pour changer
          </div>
        )}
      </div>

      {/* Boutons */}
      <div style={{ display:'flex', gap:'0.5rem' }}>
        <button type="button" onClick={() => !uploading && inputRef.current?.click()} disabled={uploading}
          style={{ flex:1, height:'44px', background:'var(--accent)', color:'var(--bg)', border:'none', borderRadius:'8px', fontSize:'0.82rem', fontWeight:500, opacity: uploading ? 0.6 : 1 }}>
          📷 {preview ? 'Changer' : 'Choisir depuis la galerie'}
        </button>
        {preview && !uploading && (
          <button type="button" onClick={() => { setPreview(''); onUploaded(''); }}
            style={{ height:'44px', padding:'0 0.8rem', background:'rgba(217,112,112,0.1)', border:'1px solid rgba(217,112,112,0.3)', color:'var(--danger)', borderRadius:'8px', fontSize:'0.82rem' }}>
            ✕
          </button>
        )}
      </div>

      {error && <p style={{ fontSize:'0.72rem', color:'var(--danger)' }}>{error}</p>}

      <input ref={inputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={e => { const f = e.target.files?.[0]; if(f) handleFile(f); }} />
    </div>
  );
}
